import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const LABELS = {
  env: { text: "Configured (environment)", cls: "bg-[#10B981] text-white" },
  db: { text: "Configured (saved here)", cls: "bg-[#10B981] text-white" },
  missing: { text: "Not configured", cls: "bg-red-500 text-white" },
};

const Row = ({ title, endpoint, source, value, onChange, testId }) => (
  <div className="border-2 border-black rounded-xl p-4 bg-white">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <div className="font-bold">{title}</div>
        <div className="text-xs text-gray-500 font-mono">{endpoint}</div>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-bold border-2 border-black ${LABELS[source]?.cls || "bg-gray-200"}`}
        data-testid={`${testId}-status`}>
        {LABELS[source]?.text || "…"}
      </span>
    </div>
    <input
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="whsec_..."
      autoComplete="off"
      data-testid={`${testId}-input`}
      className="mt-3 w-full border-2 border-black rounded-lg px-3 py-2 text-sm font-mono bg-[#FFFDF0]"
    />
  </div>
);

export const WebhookSecretsCard = () => {
  const [status, setStatus] = useState(null);
  const [subSecret, setSubSecret] = useState("");
  const [hostSecret, setHostSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/webhook-secrets");
      setStatus(data);
    } catch { /* admin guard handles redirect */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!subSecret.trim() && !hostSecret.trim()) {
      toast.error("Paste at least one signing secret");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/webhook-secrets", {
        subscription_secret: subSecret.trim() || undefined,
        hosting_secret: hostSecret.trim() || undefined,
      });
      toast.success("Webhook secret(s) saved");
      setSubSecret("");
      setHostSecret("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    }
    setSaving(false);
  };

  return (
    <div className="nb-card p-6 max-w-2xl" data-testid="webhook-secrets-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-5 h-5 text-[#FF7F50]" />
        <div className="font-display text-lg font-extrabold">Stripe webhook signing secrets</div>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Paste each endpoint&apos;s signing secret from the Stripe Dashboard (Developers → Webhooks →
        endpoint → Reveal signing secret). Values are stored server-side and never displayed again.
        Environment variables, when set, take precedence.
      </p>
      <div className="space-y-3">
        <Row title="Main plan subscriptions" endpoint="/api/webhook/stripe-subscription"
          source={status?.subscription} value={subSecret} onChange={setSubSecret}
          testId="webhook-secret-subscription" />
        <Row title="Additional Website Hosting" endpoint="/api/webhook/stripe-hosting"
          source={status?.hosting} value={hostSecret} onChange={setHostSecret}
          testId="webhook-secret-hosting" />
      </div>
      <button onClick={save} disabled={saving} data-testid="save-webhook-secrets-btn"
        className="mt-4 px-5 py-2.5 rounded-xl border-2 border-black bg-[#111] text-white font-semibold nb-press disabled:opacity-50 flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        Save secrets
      </button>
      <div className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          If a secret was ever shared insecurely, roll it in the Stripe Dashboard and save the new
          value here — it takes effect immediately, no redeploy needed.
        </span>
      </div>
    </div>
  );
};
